/**
 * Utility functions for detecting and mapping color words to actual colors
 */

export interface ColorMapping {
  color: string;
  isLight: boolean; // Whether the color needs special styling (like borders) for visibility
}

/**
 * Map of common color words to their CSS color values
 * These colors are chosen to be visually distinct and accessible
 */
const COLOR_WORD_MAP: Record<string, ColorMapping> = {
  // Reds
  red: { color: '#dc2626', isLight: false },
  crimson: { color: '#dc143c', isLight: false },
  scarlet: { color: '#ff2400', isLight: false },

  // Oranges
  orange: { color: '#ea580c', isLight: false },
  coral: { color: '#ff7f50', isLight: false },

  // Yellows
  yellow: { color: '#eab308', isLight: false },
  gold: { color: '#fbbf24', isLight: false },

  // Greens
  green: { color: '#16a34a', isLight: false },
  lime: { color: '#84cc16', isLight: false },
  emerald: { color: '#10b981', isLight: false },
  forest: { color: '#228b22', isLight: false },

  // Blues
  blue: { color: '#2563eb', isLight: false },
  navy: { color: '#1e3a8a', isLight: false },
  sky: { color: '#0ea5e9', isLight: false },
  azure: { color: '#007fff', isLight: false },
  cyan: { color: '#06b6d4', isLight: false },
  teal: { color: '#14b8a6', isLight: false },

  // Purples
  purple: { color: '#9333ea', isLight: false },
  violet: { color: '#7c3aed', isLight: false },
  indigo: { color: '#4f46e5', isLight: false },
  magenta: { color: '#d946ef', isLight: false },

  // Pinks
  pink: { color: '#ec4899', isLight: false },
  rose: { color: '#f43f5e', isLight: false },

  // Browns
  brown: { color: '#92400e', isLight: false },
  tan: { color: '#d2b48c', isLight: false },
  beige: { color: '#f5f5dc', isLight: true },

  // Grays
  gray: { color: '#6b7280', isLight: false },
  grey: { color: '#6b7280', isLight: false },
  silver: { color: '#c0c0c0', isLight: false },
  charcoal: { color: '#36454f', isLight: false },

  // Black and White
  black: { color: '#0a0a0a', isLight: false },
  white: { color: '#fafafa', isLight: true },
  ivory: { color: '#fffff0', isLight: true },
  cream: { color: '#fffdd0', isLight: true },
};

/**
 * Extract color word from a label string
 * Looks for color words that appear as standalone words (not part of other words)
 *
 * Examples:
 * - "red stripes" → "red"
 * - "white" → "white"
 * - "Red Blocks" → "red"
 * - "strawberry" → null (doesn't match because 'red' is not standalone)
 */
export function extractColorWord(label: string): string | null {
  if (!label || typeof label !== 'string') return null;

  const normalizedLabel = label.toLowerCase().trim();

  // Check each color word to see if it appears as a standalone word
  for (const colorWord of Object.keys(COLOR_WORD_MAP)) {
    // Create a regex that matches the color word as a whole word
    // \b ensures word boundaries on both sides
    const regex = new RegExp(`\\b${colorWord}\\b`, 'i');

    if (regex.test(normalizedLabel)) {
      return colorWord;
    }
  }

  return null;
}

/**
 * Get the color mapping for a label
 * Returns the color and whether it needs special styling for visibility
 */
export function getColorFromLabel(label: string): ColorMapping | null {
  const colorWord = extractColorWord(label);
  if (!colorWord) return null;

  return COLOR_WORD_MAP[colorWord] || null;
}

/**
 * Compute the effective color for a tw-block line based on its label
 * If the label contains a color word, returns that color
 * Otherwise, returns the original/default color
 */
export function computeBlockColor(label: string | undefined, defaultColor: string): ColorMapping {
  if (!label) {
    return { color: defaultColor, isLight: false };
  }

  const colorFromLabel = getColorFromLabel(label);

  if (colorFromLabel) {
    return colorFromLabel;
  }

  return { color: defaultColor, isLight: false };
}
